/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useToast } from 'src/hooks/use-toast';
import { useResumeUpload } from 'src/hooks/useResumeUpload';
import { OTPVerification } from 'src/components/OTPVerification';
import { useGenericOTP } from 'src/hooks/useGenericOTP';
import { ResumeUpload } from './ResumeUpload';
import { getUser, clearAuth } from 'src/utils/authStorage';
import {
  getExternalSignupToken,
  setExternalSignupToken,
} from 'src/utils/externalSignupToken';
import plainAxios from 'src/services/axios.plain';
import { createEndpoint, API_ENDPOINTS } from 'src/services/api/endpoints';
import { trackEvent, setGaUserId, getTrafficSource } from 'src/utils/analytics';
import { useFormAbandonmentTracking } from 'src/hooks/useFormAbandonmentTracking';

export function UploadResume({ onAuthSuccess }) {
  React.useLayoutEffect(() => {
    clearAuth();
    localStorage.removeItem('refreshToken');
  }, []);

  const navigate = useNavigate();
  const { toast } = useToast();
  const { token: tokenFromPath } = useParams();
  const [searchParams] = useSearchParams();
  const tokenFromQuery = searchParams.get('token');
  const [externalSignupToken, setExternalSignupTokenState] = useState(
    () => tokenFromQuery || tokenFromPath || getExternalSignupToken()
  );
  const [submitError, setSubmitError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showResumeUpload, setShowResumeUpload] = useState(false);

  // Capture external-source token (NOT an auth token) for follow-up API call
  useEffect(() => {
    const incoming = tokenFromQuery || tokenFromPath;
    if (incoming && incoming !== externalSignupToken) {
      setExternalSignupToken(incoming);
      setExternalSignupTokenState(incoming);
    }
    // If we already have one in state/storage, keep it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenFromQuery, tokenFromPath]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      firstname: '',
      lastname: '',
      contact: '',
      resume: null,
    },
  });

  const contactValue = watch('contact');
  const firstnameValue = watch('firstname');
  const lastnameValue = watch('lastname');
  const isProfileInfoReadyForOTP =
    !!firstnameValue?.trim() && !!lastnameValue?.trim();

  const contactOtpHook = useGenericOTP(
    contactValue,
    'contact',
    null,
    firstnameValue,
    lastnameValue,
    null,
    toast,
    'signUp',
    !!onAuthSuccess
  );
  const resumeHook = useResumeUpload();
  const signupTrackedRef = useRef(false);
  const currentSignupSection = contactOtpHook.showOTPInput
    ? 'otp'
    : contactOtpHook.isOTPSent
    ? 'phone'
    : 'name';
  const { markCompleted } = useFormAbandonmentTracking(
    'Signup',
    currentSignupSection,
    !!firstnameValue?.trim() || !!lastnameValue?.trim() || !!contactValue?.trim()
  );

  // Fetch basic info from token and prefill form
  useEffect(() => {
    const token = tokenFromQuery || tokenFromPath || externalSignupToken;
    if (!token) return;

    const fetchBasicInfo = async () => {
      try {
        const endpoint = createEndpoint(API_ENDPOINTS.RESUME.GET_BASIC_INFO, {
          token,
        });
        const response = await plainAxios.get(endpoint);
        
        // Prefill form fields if data is available
        // Handle both nested (response.data.data) and direct (response.data) structures
        const data = response.data?.data || response.data;
        if (data) {
          // Handle various field name formats (snake_case, camelCase, UPPERCASE)
          const firstname =
            data.first_name || data.firstname || data.firstName || data.FIRSTNAME;
          const lastname =
            data.last_name || data.lastname || data.lastName || data.LASTNAME;
          const phone =
            data.phone ||
            data.phone_number ||
            data.mobile ||
            data.mobile_number ||
            data.contact ||
            data.PHONE ||
            data.Phone;
          
          if (firstname) setValue('firstname', firstname);
          if (lastname) setValue('lastname', lastname);
          if (phone) setValue('contact', phone);
        }
      } catch (error) {
        // Silently fail - no toast or UI feedback should be shown
        // Just don't prefill the form if the API call fails
        // This is intentional - we don't want to show errors for optional prefilling
      }
    };

    fetchBasicInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenFromQuery, tokenFromPath, externalSignupToken, setValue]);

  // Show success message when OTP is verified, then transition to resume upload or proceed
  useEffect(() => {
    if (contactOtpHook.verified) {
      if (!signupTrackedRef.current) {
        signupTrackedRef.current = true;
        const verifiedUser = getUser();
        if (verifiedUser?.cid) setGaUserId(verifiedUser.cid);
        trackEvent('signup_otp_verified', 'Signup', getTrafficSource());
      }
      // Check if resume is already processed
      const resumeProcessed = contactOtpHook.resumeProcessed ?? false;

      if (resumeProcessed === true) {
        // Resume already processed, skip upload and proceed to next page
        setShowSuccessMessage(false);
        setShowResumeUpload(false);
        markCompleted();
        trackEvent('signup_completed', 'Signup', getTrafficSource());

        // Proceed to next page after a brief delay
        setTimeout(() => {
          const userData = getUser();
          if (onAuthSuccess) {
            onAuthSuccess();
          } else if (userData?.emailVerified) {
            navigate('/dashboard');
          } else {
            navigate('/create-link');
          }
        }, 500);
      } else {
        // Resume not processed, show upload form
        setShowSuccessMessage(true);
        const timer = setTimeout(() => {
          setShowSuccessMessage(false);
          setShowResumeUpload(true);
        }, 1500);
        return () => clearTimeout(timer);
      }
    } else {
      setShowSuccessMessage(false);
      setShowResumeUpload(false);
    }
  }, [contactOtpHook.verified, contactOtpHook.resumeProcessed, onAuthSuccess, navigate]);

  const onSubmit = async (data) => {
    trackEvent('resume_upload_attempt', 'Signup', 'resume');
    try {
      setIsSubmitting(true);
      setSubmitError('');
      const userData = getUser();
      const phoneNumber = contactValue || '';
      const resumeUrl = await resumeHook.uploadResume(userData.cid, phoneNumber);

      if (resumeUrl) {
        trackEvent('resume_upload_success', 'Signup', 'resume');
        markCompleted();
        trackEvent('signup_completed', 'Signup', getTrafficSource());
        toast({
          message: 'Your profile has been submitted successfully',
          variant: 'success',
        });

        if (onAuthSuccess) {
          onAuthSuccess();
        } else if (userData.emailVerified) {
          navigate('/dashboard');
        } else {
          navigate('/create-link');
        }
      } else {
        throw new Error(resumeUrl.message || 'Failed to submit form');
      }
    } catch (error) {
      trackEvent('resume_upload_error', 'Signup', 'resume');
      setSubmitError(error.message);
      toast({ message: error.message, variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      style={{ display: 'flex', flexDirection: 'column', gap: 14, textAlign: 'left' }}
      noValidate
    >
      {/* ── Name Fields and Phone OTP — only show before verification ── */}
      {!contactOtpHook.verified && (
        <>
          {/* ── Name Fields Grid ── */}
          <div className="tb-form-grid">
            {/* First Name */}
            <div className="tb-field">
              <label htmlFor="firstname" className="tb-label">
                First Name <span className="tb-req">*</span>
              </label>
              <input
                id="firstname"
                placeholder=""
                className={`tb-input${errors.firstname ? ' error' : ''}`}
                {...register('firstname')}
                autoComplete="given-name"
              />
              {errors.firstname && (
                <div className="tb-field-error">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <span>{errors.firstname.message}</span>
                </div>
              )}
            </div>

            {/* Last Name */}
            <div className="tb-field">
              <label htmlFor="lastname" className="tb-label">
                Last Name <span className="tb-req">*</span>
              </label>
              <input
                id="lastname"
                placeholder=""
                className={`tb-input${errors.lastname ? ' error' : ''}`}
                {...register('lastname')}
                autoComplete="family-name"
              />
              {errors.lastname && (
                <div className="tb-field-error">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <span>{errors.lastname.message}</span>
                </div>
              )}
            </div>
          </div>

          {/* ── Phone + OTP ── */}
          <OTPVerification
            type="contact"
            register={register}
            errors={errors}
            value={watch('contact')}
            disabled={!isProfileInfoReadyForOTP}
            verified={contactOtpHook.verified}
            isOTPSent={contactOtpHook.isOTPSent}
            isOTPLoading={contactOtpHook.isOTPLoading}
            showOTPInput={contactOtpHook.showOTPInput}
            isVerifyingOTP={contactOtpHook.isVerifyingOTP}
            otpError={contactOtpHook.otpError}
            canResend={contactOtpHook.canResend}
            resendTimeout={contactOtpHook.resendTimeout}
            handleSendOTP={contactOtpHook.handleSendOTP}
            handleVerifyOTP={contactOtpHook.handleVerifyOTP}
          />
        </>
      )}

      {/* ── Account created success message ── */}
      <div className={`tb-step-success${showSuccessMessage ? ' show' : ''}`}>
        <div className="tb-step-success-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2E8B6A" strokeWidth="2.5" strokeLinecap="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <div>
          <div className="tb-step-success-title">Account created!</div>
          <div className="tb-step-success-sub">Now upload your resume to get matched.</div>
        </div>
      </div>

      {/* ── Resume Upload — only after success message transition ── */}
      {showResumeUpload && (
        <>
          <h3
            style={{
              fontFamily: "'Sora', sans-serif",
              fontSize: '0.8rem',
              fontWeight: 700,
              color: '#585f67',
              marginTop: 0,
            }}
          >
            Complete your profile
          </h3>
          <ResumeUpload
            register={register}
            errors={errors}
            contactVerified={contactOtpHook.verified}
            isFileUploading={resumeHook.isFileUploading}
            isUploadReady={resumeHook.isUploadReady}
            fileUploadError={resumeHook.fileUploadError}
            handleFileSelection={resumeHook.handleFileSelection}
            validateFile={resumeHook.validateFile}
            clearFile={resumeHook.clearFile}
          />
        </>
      )}

      {submitError && (
        <div className="tb-field-error">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>{submitError}</span>
        </div>
      )}

      {/* ── Submit ── */}
      {showResumeUpload && (
        <button
          type="submit"
          className="tb-submit-btn"
          disabled={isSubmitting || !resumeHook.selectedFile}
        >
        {isSubmitting ? (
          <>
            <span className="tb-spinner" />
            Submitting...
          </>
        ) : (
          'Submit Resume'
        )}
        </button>
      )}

      {/* ── Trust Row ── */}
      <div className="tb-trust-row">
        <div className="tb-trust-item">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0110 0v4" />
          </svg>
          256-bit encrypted
        </div>
        {/* <div className="tb-trust-item">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          GDPR compliant
        </div> */}
        <div className="tb-trust-item">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          2 min setup
        </div>
      </div>
    </form>
  );
}

export default UploadResume;
